import React from 'react';
import type { ActionType, StateType } from 'typesafe-actions';

import type * as actions from './actions.tsx';
import reducer, { initialState } from './reducer.tsx';

type State = StateType<typeof reducer>;
type Dispatch = (action: ActionType<typeof actions>) => void;

export interface Store {
  getState: () => State;
  dispatch: Dispatch;
  subscribe: (listener: () => void) => () => void;
}

// external store so a dispatch re-renders only the consumers whose selected
// value actually changed, instead of every component under the provider
export const createStore = (): Store => {
  let state = initialState;
  const listeners = new Set<() => void>();

  return {
    getState: () => state,
    dispatch: (action) => {
      const next = reducer(state, action);
      if (next === state) {
        return;
      }
      state = next;
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

const context = React.createContext<Store>(createStore());

export const useSelector = <In extends unknown[], Out>(selector: (state: State, ...args: In) => Out, args: In): Out => {
  const store = React.useContext(context);
  const cacheRef = React.useRef<{ selector: (state: State, ...args: In) => Out; state: State; args: In; result: Out } | null>(null);

  // getSnapshot must return a stable value while neither state nor args
  // change, otherwise useSyncExternalStore re-renders in a loop
  const getSnapshot = () => {
    const state = store.getState();
    const cache = cacheRef.current;
    if (
      cache &&
      cache.selector === selector &&
      cache.state === state &&
      cache.args.length === args.length &&
      cache.args.every((arg, i) => arg === args[i])
    ) {
      return cache.result;
    }
    const result = selector(state, ...args);
    cacheRef.current = { selector, state, args, result };
    return result;
  };

  return React.useSyncExternalStore(store.subscribe, getSnapshot);
};

export const useDispatch = (): Dispatch => React.useContext(context).dispatch;

export default context;
